import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SITE } from "@/lib/site";

const CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    intro: `${SITE.name} ("we", "us") respects your privacy. This Policy explains how we collect, use and safeguard the information you share with us.`,
    sections: [
      { h: "Information We Collect", p: "Personal information you provide voluntarily (name, email, phone, investment preferences) when you contact us, register for services, or submit forms. We also collect non-personal usage data via cookies and analytics tools." },
      { h: "How We Use Your Information", p: "To respond to your enquiries, deliver mutual fund distribution services, fulfil regulatory obligations (KYC, AMFI compliance), and send relevant updates about your investments or products you've consented to receive." },
      { h: "Data Sharing", p: "We share data only with regulated entities (AMCs, RTAs like CAMS/KFintech, AMFI/SEBI) required to execute investments on your behalf. We never sell personal information to third parties." },
      { h: "Data Security", p: "We use encryption-in-transit, restricted access controls and secure infrastructure to protect your information. However, no internet transmission is 100% secure." },
      { h: "Your Rights", p: "You may request access, correction or deletion of your personal data at any time by writing to us at " + SITE.email + "." },
      { h: "Cookies", p: "Our website uses cookies to enhance user experience and measure analytics. You may disable cookies in your browser settings." },
      { h: "Changes to this Policy", p: "We may update this Policy from time to time. Material changes will be communicated on this page." },
    ],
  },
  "terms-of-use": {
    title: "Terms of Use",
    intro: `By accessing this website, you agree to be bound by these Terms of Use. If you do not agree, please refrain from using the website.`,
    sections: [
      { h: "Eligibility", p: "You must be at least 18 years of age and competent to contract under Indian law to use this website or our services." },
      { h: "No Personalised Suggestions", p: "Content on this website is for general information only and does not constitute investment suggestions, an offer or solicitation. Always read scheme documents and consult your financial professional before investing." },
      { h: "Distributor Capacity", p: "Axomind Asset Services LLP acts as an AMFI Registered Mutual Fund Distributor. We are not portfolio managers, investment advisers, brokers or banks." },
      { h: "Intellectual Property", p: "All content, logos, and design on this website are the property of Axomind Asset Services LLP and may not be reproduced without permission." },
      { h: "Third-Party Links", p: "We may link to external websites for your convenience. We are not responsible for the content or practices of those sites." },
      { h: "Limitation of Liability", p: "We are not liable for any losses arising from your use of this website or reliance on its content. Mutual fund investments are subject to market risks." },
      { h: "Governing Law", p: "These Terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the courts at Mumbai." },
    ],
  },
  "risk-disclosure": {
    title: "Risk Disclosure",
    intro: `Investments in mutual funds and securities markets are subject to market risks. Please read this Risk Disclosure carefully before investing.`,
    sections: [
      { h: "Market Risk", p: "The value of mutual fund units fluctuates with market conditions. There is no guarantee of returns and you may receive less than the amount invested." },
      { h: "Credit & Interest Rate Risk (Debt Funds)", p: "Debt mutual funds are exposed to credit risk (issuer default) and interest rate risk (NAVs may fall when rates rise)." },
      { h: "Equity Risk", p: "Equity mutual funds carry volatility risk and are suitable only for investors who can stay invested through market cycles." },
      { h: "Liquidity Risk", p: "Some schemes (e.g., close-ended funds, illiquid securities) may face redemption restrictions or wider bid-ask spreads." },
      { h: "Concentration Risk", p: "Sectoral, thematic or single-stock concentrated schemes carry higher volatility than diversified portfolios." },
      { h: "Past Performance", p: "Past performance is not indicative of future returns. Historical returns do not guarantee future outcomes." },
      { h: "Taxation Risk", p: "Tax rules applicable to mutual fund schemes may change. Consult a qualified tax professional before relying on tax outcomes." },
      { h: "Read Scheme Documents", p: "Before investing, read the Scheme Information Document (SID), Statement of Additional Information (SAI) and Key Information Memorandum (KIM)." },
    ],
  },
  "disclaimer": {
    title: "Disclaimer",
    intro: `The information on this website is for educational and informational purposes only and should not be construed as personalised investment suggestions.`,
    sections: [
      { h: "General Disclaimer", p: "Axomind Asset Services LLP is an AMFI Registered Mutual Fund Distributor (ARN-365843). The content on this website does not constitute investment suggestions, legal suggestions, tax suggestions or any solicitation to buy or sell mutual funds or any financial product." },
      { h: "Not an Investment Adviser", p: "Axomind Asset Services LLP is NOT a SEBI-registered Investment Adviser. We do not provide investment advisory or financial planning services. Our role is limited to distribution of mutual fund schemes and incidental investor education, in line with the AMFI Code of Conduct for Mutual Fund Distributors." },
      { h: "Mutual Fund Risk", p: "Mutual fund investments are subject to market risks. Read all scheme related documents carefully before investing." },
      { h: "No Guarantee of Returns", p: "Returns from mutual fund schemes are not guaranteed and depend on market performance. Past performance is not indicative of future results." },
      { h: "Commission Disclosure", p: "We distribute Regular Plans of mutual fund schemes and are remunerated through trail commissions paid by Asset Management Companies (AMCs) from the scheme's expense ratio, as per regulatory norms. Scheme-wise commission structures are available on request. We do not charge investors any separate fee." },
      { h: "Accuracy of Information", p: "While we make best efforts to keep information accurate and up to date, we make no warranties about completeness or reliability and disclaim liability for errors or omissions." },
      { h: "External Links", p: "Links to third-party websites are provided for convenience. We do not endorse and are not responsible for their content." },
      { h: "Investor Grievances", p: "For any complaints or grievances, please contact us at " + SITE.email + " or call " + SITE.phone + "." },
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const data = CONTENT[slug];

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold">Document not found</h1>
          <Link to="/" className="mt-4 inline-block text-[#D4AF37]">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={`legal-${slug}`}>
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-28 pb-24">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37] transition"
        >
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <p className="mt-10 text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Legal</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-white mt-3 leading-[1.05]">
          {data.title}
        </h1>
        <p className="mt-5 text-gray-400 leading-relaxed">{data.intro}</p>

        <div className="mt-10 space-y-8">
          {data.sections.map((s, i) => (
            <section key={i} className="glass rounded-2xl p-6">
              <h2 className="font-display font-semibold text-xl text-white">{s.h}</h2>
              <p className="mt-3 text-gray-400 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 text-xs text-gray-500">
          Last updated: {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
        </div>
      </div>
    </div>
  );
}
